import SSEWorker from "@/service/worker/sseWorker.worker.js";
import store from "@/store";

/**
 * SSE transport for BackendSyncService using Mercure/EventSource.
 * Runs the EventSource connection in a dedicated Web Worker to avoid
 * main-thread throttling when the screen is off (Android Chrome).
 */
class BackendSyncSSEService {
	constructor({ topics = [], onReconnectNeeded = null } = {}) {
		const { sseUrl } = store.state.config;

		this.sseUrl = sseUrl;
		this.topics = topics;
		this.onReconnectNeeded = onReconnectNeeded;
		this.listeners = [];

		this.worker = new SSEWorker();
		this.worker.onmessage = this.handleWorkerMessage.bind(this);

		this.handleOnline = this.handleOnline.bind(this);
		this.handleOffline = this.handleOffline.bind(this);
		window.addEventListener("online", this.handleOnline);
		window.addEventListener("offline", this.handleOffline);
	}

	handleOnline() {
		console.log("[BackendSyncSSEService] Device came online - refreshing Mercure auth before reconnect");
		if (this.onReconnectNeeded) {
			this.onReconnectNeeded();
			return;
		}
		this.worker.postMessage({ type: "forceReconnect" });
	}

	handleOffline() {
		console.log("[BackendSyncSSEService] Device went offline");
	}

	connect() {
		this.worker.postMessage({
			type: "connect",
			payload: {
				sseUrl: this.sseUrl,
				topics: this.topics,
			},
		});
	}

	openConnection() {
		this.worker.postMessage({ type: "openConnection" });
	}

	disconnect() {
		this.worker.postMessage({ type: "disconnect" });
		window.removeEventListener("online", this.handleOnline);
		window.removeEventListener("offline", this.handleOffline);
	}

	// eslint-disable-next-line no-unused-vars
	sendMessage(message) {
		// SSE is unidirectional — client-to-server goes through HTTP endpoints.
	}

	handleWorkerMessage(event) {
		const { type, data, error } = event.data;

		switch (type) {
			case "connected":
			case "connecting":
			case "disconnected":
			case "error":
				this.listeners.forEach((listener) => {
					listener(type, error || data);
				});
				break;

			case "message":
				this.listeners.forEach((listener) => {
					listener("message", data);
				});
				break;

			case "reconnectNeeded":
				if (this.onReconnectNeeded) {
					this.onReconnectNeeded();
				}
				break;

			default:
				console.warn("[BackendSyncSSEService] Unknown message from SSE worker:", event.data);
				break;
		}
	}

	addMessageListener(listener) {
		this.listeners.push(listener);
	}

	removeMessageListener(listener) {
		this.listeners = this.listeners.filter((l) => l !== listener);
	}
}

export default BackendSyncSSEService;
